import React, { useCallback, useEffect, useState } from "react";
import { BsArrowDown } from "react-icons/bs";
import { useFormik } from "formik";
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import Spinner from "react-bootstrap/esm/Spinner";
import DataTable from "react-data-table-component";
import { FaSearch } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { RiFilterLine, RiFilterOffLine } from "react-icons/ri";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonImagePermission from "../../components/UI/Button/ButtonImagePermission";
import TextInputFormik from "../../components/UI/TextInput/TextInputFormik";
import useApiClient, { apiRoutes } from "../../hooks/apiClient";
import TextSpinner from "../../components/UI/Spinner/TextSpinner";
import SelectInputCachedFormik from "../../components/UI/SelectInput/SelectInputCachedFormik";
import funcionalidades from "../../context/AuthContext.funcionalidades";
import EfisModal from "../../components/UI/EfisModal/EfisModal";
import HtmlToString from "../../components/Formatters/HtmlToString";
import constants from "../Constants";

const CarregandoView = (
  <span>
    <Spinner animation="border" role="status" /> Carregando ...
  </span>
);

const removeCell = (row, setShowDesassociarConfirm) => (
  <ButtonImagePermission
    permission={funcionalidades.ordemservico.deletarAltExecutor}
    className="btn-lg text-danger pa-0 p-0"
    onClick={() =>
      setShowDesassociarConfirm({
        show: true,
        body: `Deseja remover o executor ${row.nomeFormal}?`,
        context: row.codigoExecutor,
      })
    }
    buttonImage={<MdClose />}
    tooltipImage="Remover Auditor"
  />
);

const columns = [
  {
    name: "Setor",
    selector: (row) => row.siglaOrgao,
    center: true,
    sortable: true,
  },
  {
    name: "Nome",
    selector: (row) => row.nomeFormal,
    width: "30%",
    sortable: true,
  },
  {
    name: "Matrícula",
    selector: (row) => row.matricula,
    center: true,
  },
  {
    name: "Qtd Ações",
    selector: (row) => row.quantidadeOs,
    center: true,
    sortable: true,
  },
  {
    name: "Tipos de Ação Fiscal",
    cell: (row) =>
      row.quantidadeTipoAf ? (
        <span>
          <HtmlToString html={row.quantidadeTipoAf} />
        </span>
      ) : (
        ""
      ),
    width: "35%",
    center: true,
  },
];

const validationSchema = Yup.object().shape({
  sequencialOrgao: Yup.string().default(""),
  nomeFormal: Yup.string().default(""),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const ListaExecutores = ({ formik: propsFormik, field, dadosOs, origem }) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParameters, setSearchParameters] = useState({});
  const [inputSearchArgument, setInputSearchArgument] = useState("");
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [executorAssociado, setExecutorAssociado] = useState([]);
  const [showDesassociarConfirm, setShowDesassociarConfirm] = useState({
    show: false,
    body: null,
    context: null,
  });

  const { request: requestConsulta, data: dataConsulta, errors: errorConsulta, isLoading } = useApiClient();

  const formik = useFormik({
    initialValues,
    validationSchema,
  });

  const updateFormik = (state) => {
    propsFormik.setFieldValue(field, state.selectedRows);
  };

  const handlePageChange = useCallback((newPageNumber) => {
    setPageNumber(newPageNumber);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  useEffect(() => {
    requestConsulta(apiRoutes.fiscalizacao.ordemservico.consultaExecutores, {
      ...searchParameters,
      pageSize,
      pageNumber,
    });
  }, [requestConsulta, searchParameters, pageSize, pageNumber]);

  useEffect(() => {
    if (dataConsulta && dataConsulta.result) {
      if (dataConsulta.result.length && !dataConsulta.result[0].totalRows) {
        console.error(`[DEV] A propriedade "totalRows" é obrigatória nos resultados paginados.`);
        return;
      }
      setRows(dataConsulta.result || []);
      setTotalRows(dataConsulta.result.length ? dataConsulta.result[0].totalRows : 0);
    }

    if (errorConsulta) {
      toast.error(errorConsulta.message);
    }
  }, [dataConsulta, errorConsulta]);

  const { request: requestAssociados, data: dataAssociados, isLoading: isLoadingAssociados } = useApiClient();

  useEffect(() => {
    if (origem === "atividadeOs") {
      requestAssociados(apiRoutes.fiscalizacao.ordemservico.consultaExecutorAf, { sqAcaoFiscal: dadosOs?.acaoFiscal });
    }
  }, [requestAssociados, origem, dadosOs?.acaoFiscal]);

  const { setValues } = propsFormik;

  useEffect(() => {
    if (dataAssociados) {
      setExecutorAssociado(dataAssociados.result);

      setValues((curr) => ({
        ...curr,
        totalExecutores: dataAssociados?.result ? dataAssociados?.result.length : 0,
      }));
    }
  }, [dataAssociados, setValues]);

  const {
    request: deleteItemRequest,
    isLoading: deleteItemIsLoading,
    data: deleteItemData,
    errors: deleteItemErrors,
  } = useApiClient();

  useEffect(() => {
    if (origem === "atividadeOs") {
      if (!deleteItemData && !deleteItemErrors) return;

      if (deleteItemData) {
        toast.success("Executor desassociado com sucesso.");
        const sqAcaoFiscal = dadosOs?.acaoFiscal;
        requestAssociados(apiRoutes.fiscalizacao.ordemservico.consultaExecutorAf, { sqAcaoFiscal });
      }

      if (deleteItemErrors) {
        toast.error(deleteItemErrors);
      }

      setShowDesassociarConfirm({ show: false });
    }
  }, [deleteItemData, deleteItemErrors, setShowDesassociarConfirm, requestAssociados, dadosOs?.acaoFiscal, origem]);

  const columnsAssociados = [
    ...columns,
    {
      name: "Ações",
      selector: "",
      width: "10%",
      center: true,
      cell: (row) => removeCell(row, setShowDesassociarConfirm),
    },
  ];

  return (
    <>
      <EfisModal
        show={showDesassociarConfirm.show}
        body={showDesassociarConfirm.body}
        loadingConfirmationAction={deleteItemIsLoading}
        closeHandler={() => setShowDesassociarConfirm({ show: false })}
        confirmationHandler={() => {
          const params = {
            sequencialAcaoFiscal: dadosOs.acaoFiscal,
            codigoExecutor: showDesassociarConfirm.context,
          };
          deleteItemRequest(apiRoutes.fiscalizacao.ordemservico.excluirExecutorAf, params);
        }}
      />

      {origem === "atividadeOs" && (
        <div className="row">
          <div className="col-lg-12">
            <div className="card shadow mb-4">
              <div className="card-header pt-3 pb-0 flex-row w-100">
                <h6 className="m-0 font-weight-bold text-primary flex-grow-1 py-3">Executores associados à OS </h6>
              </div>

              <div className="card-body">
                <DataTable
                  noHeader
                  title="Executores associados à OS"
                  keyField="id"
                  columns={columnsAssociados}
                  data={executorAssociado}
                  progressPending={isLoadingAssociados}
                  noDataComponent="Nenhum registro para exibir"
                  progressComponent={CarregandoView}
                  defaultSortFieldId={1}
                  sortIcon={<BsArrowDown />}
                  pagination
                  sortable
                  onSelectedRowsChange={(state) => updateFormik(state)}
                  pointerOnHover
                  paginationComponentOptions={constants.paginationComponentOptions}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header pt-3 pb-0 flex-row w-100">
              <Form.Group as={Row} className="d-flex flex-row">
                {<h6 className="font-weight-bold text-primary flex-grow-1 ml-2 mt-2"> </h6>}

                {!showAdvancedSearch && (
                  <ButtonGroup>
                    <Form.Control
                      className="w-auto border-top-right-0 border-bottom-right-0"
                      type="text"
                      placeholder="Digite o nome"
                      value={inputSearchArgument}
                      onChange={(e) => setInputSearchArgument(e.target.value)}
                    />

                    <Button
                      className="btn-search-inside btn-md mr-3"
                      onClick={() => {
                        setPageNumber(1);
                        setSearchParameters({ searchArgument: inputSearchArgument });
                      }}
                    >
                      <FaSearch />
                    </Button>
                  </ButtonGroup>
                )}

                {formik && (
                  <Button
                    className="btn btn-primary btn-md mr-2"
                    tootip="Busca Avançada"
                    onClick={() => {
                      setShowAdvancedSearch((curr) => {
                        formik.resetForm();
                        return !curr;
                      });
                    }}
                  >
                    {showAdvancedSearch ? <RiFilterOffLine /> : <RiFilterLine />}
                  </Button>
                )}
              </Form.Group>

              {showAdvancedSearch && (
                <>
                  <hr />
                  <Row>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Setor</Form.Label>
                      <SelectInputCachedFormik formik={formik} field="sequencialOrgao" tipoItem="orgaos-geral-os" />
                    </Form.Group>

                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Nome Executor</Form.Label>
                      <TextInputFormik formik={formik} field="nomeFormal" />
                    </Form.Group>
                  </Row>
                  <Form.Group as={Row} className="d-flex flex-row">
                    <Col sm="12">
                      <Button
                        className="btn btn-primary btn-md mr-2"
                        onClick={() => {
                          setPageNumber(1);
                          setSearchParameters({ ...formik.values });
                        }}
                      >
                        Buscar
                      </Button>
                    </Col>
                  </Form.Group>
                </>
              )}

              <Form.Label className="small">
                {`Total selecionado: ${propsFormik.values[field] ? propsFormik.values[field].length : 0}`}
              </Form.Label>
              <br />
              <Form.Label className="text-danger small">
                {propsFormik.errors[field] ? propsFormik.errors[field] : ""}
              </Form.Label>
            </div>
            <div className="card-body">
              <DataTable
                noHeader
                keyField="codigoExecutor"
                columns={columns}
                data={rows}
                progressPending={isLoading}
                noDataComponent="Nenhum registro para exibir"
                progressComponent={<TextSpinner />}
                defaultSortFieldId={1}
                sortIcon={<BsArrowDown />}
                sortable
                selectableRows
                onSelectedRowsChange={(state) => updateFormik(state)}
                pointerOnHover
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                pagination
                paginationComponentOptions={constants.paginationComponentOptions}
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={10}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListaExecutores;
